import type {
  MediaController,
  MediaControllerEventHandlers,
  MediaControllerInitializeOptions,
  MediaRecorderWaveformSamples,
  MediaTranscriberEventHandlers,
} from "@/store/audio/recording/audio-recording.types";
import { DesktopMediaTranscriber } from "@/store/audio/recording/media-transcriber/desktop-media-transcriber";
import { DesktopCaptureService } from "@/modules/audio-recording/capture";
import { DESKTOP_PCM_SAMPLE_RATE } from "@/modules/audio-recording/capture";
import type { AudioLevelProbe } from "@/modules/audio-recording/audio-level-probe";
import { DesktopMediaVisualizer } from "@/store/audio/recording/media-visualizer/desktop-media-visualizer";
import { DesktopPcmProbe } from "@/modules/audio-recording/visualizers";
import { TranscriptionStatus } from "@/store/audio/recording/media-types";
import { TranscriptionMonitor } from "@/store/audio/recording/media-transcriber/transcription-monitor";
import { PreparedRecording } from "@/store/audio/recording/prepared-recording-controller";
import { logger } from "@/modules/logger";
import type { DesktopWaveformUpdate } from "@/modules/audio-recording/desktop-waveform-update";
import { DesktopAudioRecordingStrategy } from "@/modules/electron/types";
import {
  type DesktopControllerKind,
  DesktopRecordingRestartRequiredError,
  normalizeDesktopControllerError,
} from "@/store/audio/recording/media-controller/desktop-controller-errors";
import { createDesktopRecordingInitializationTimeoutError } from "@/store/audio/recording/media-controller/desktop-start-timeout-diagnostics";
import {
  AudioRecordingError,
  AudioRecordingErrorSource,
  AudioRecordingErrorStage,
} from "@/domains/errors";

export type DesktopArtifactMediaControllerOptions = {
  eventHandlers: MediaControllerEventHandlers;
  preparedRecording?: PreparedRecording | null;
};

const DESKTOP_ARTIFACT_MEDIA_CONTROLLER_KIND: DesktopControllerKind =
  "desktop_artifact_media_controller";

/**
 * Desktop recordings that are owned by Electron still need renderer-side transcript state,
 * upload coordination, and waveform updates. This controller keeps those concerns in the
 * renderer while leaving the raw audio bytes and final upload in Electron.
 */
export class DesktopArtifactMediaController implements MediaController {
  readonly kind = DESKTOP_ARTIFACT_MEDIA_CONTROLLER_KIND;
  private readonly handlers: MediaControllerEventHandlers;
  private readonly captureService: DesktopCaptureService;
  private readonly transcriber: DesktopMediaTranscriber;
  private readonly visualizerEngine = new DesktopMediaVisualizer();
  private visualizer: AudioLevelProbe | null = null;
  private probe: DesktopPcmProbe | null = null;
  private isInitialized = false;
  private readonly abortController = new AbortController();
  private readonly transcriptionMonitor = new TranscriptionMonitor({ maxReconnectAttempts: 5 });

  constructor({ eventHandlers, preparedRecording }: DesktopArtifactMediaControllerOptions) {
    this.handlers = eventHandlers;

    const transcriberRef: { current: DesktopMediaTranscriber | null } = { current: null };

    const emitError = (error: unknown, stage?: AudioRecordingErrorStage) => {
      const wrapped =
        error instanceof AudioRecordingError
          ? error
          : new AudioRecordingError({
              source: AudioRecordingErrorSource.RecallDesktop,
              stage,
              message: error instanceof Error ? error.message : String(error),
              originalError: error instanceof Error ? error : undefined,
            });

      this.handlers.onError?.({ error: wrapped });
    };

    this.captureService = new DesktopCaptureService(preparedRecording ?? null, {
      onTranscriptPartial: ({ channel, partialTurn }) => {
        transcriberRef.current?.handleTranscriptPartial({ channel, partialTurn });
      },
      onTranscriptFinal: ({ channel, finalTurn }) => {
        transcriberRef.current?.handleTranscriptFinal({ channel, finalTurn });
      },
      onWaveformUpdate: ({ update }) => {
        this.pushWaveformUpdate(update);
      },
      onDebugEvent: ({ data, kind }) => {
        this.handlers.onDebugEvent?.({ data, kind });
      },
    });

    const transcriber = new DesktopMediaTranscriber({ emitError });
    transcriberRef.current = transcriber;
    this.transcriber = transcriber;

    const transcriberHandlers: MediaTranscriberEventHandlers = {
      onTranscriptPartial: ({ partialTurn }) => {
        this.emitTranscriptionStatus(this.transcriptionMonitor.markConnected());
        this.handlers.onTranscriptPartial?.({ partialTurn });
      },
      onTranscriptFinal: ({ finalTurn }) => {
        this.emitTranscriptionStatus(this.transcriptionMonitor.markConnected());
        this.handlers.onTranscriptFinal?.({ finalTurn });
      },
      onError: ({ error }) => {
        const wrapped =
          error instanceof AudioRecordingError
            ? error
            : new AudioRecordingError({
                source: AudioRecordingErrorSource.RecallDesktop,
                stage: AudioRecordingErrorStage.TranscriberEvent,
                message: error instanceof Error ? error.message : String(error),
                originalError: error instanceof Error ? error : undefined,
              });

        this.handlers.onError?.({ error: wrapped });
        this.emitTranscriptionStatus(this.transcriptionMonitor.markDisconnected());
      },
      onStatusChange: ({ status }) => {
        this.handlers.onTranscriptionStatusChange?.({ status });
      },
    };

    this.transcriber.setEventHandlers(transcriberHandlers);
  }

  private static readonly DEFAULT_INITIALIZATION_TIMEOUT_MS = 8_000;

  async initializeAndStart(options: MediaControllerInitializeOptions): Promise<void> {
    const timeoutMs =
      options.initializationTimeoutMs ??
      DesktopArtifactMediaController.DEFAULT_INITIALIZATION_TIMEOUT_MS;
    let initializationTimeout: ReturnType<typeof setTimeout> | null = null;

    try {
      let initialized = false;

      await Promise.race([
        this._initializeAndStart(options),
        new Promise<never>((_, reject) => {
          initializationTimeout = setTimeout(() => {
            if (initialized) {
              return;
            }

            this.abortController.abort();
            void createDesktopRecordingInitializationTimeoutError({
              controllerKind: DESKTOP_ARTIFACT_MEDIA_CONTROLLER_KIND,
              logMessage: "[DesktopArtifactMediaController] Initialization timed out",
              options,
              preparationProgress: this.captureService.getPreparationProgress(),
              recordingStrategy: DesktopAudioRecordingStrategy.DesktopArtifact,
              timeoutMs,
            }).then(reject);
          }, timeoutMs);
        }),
      ]);

      initialized = true;
    } catch (error) {
      if (error instanceof DesktopRecordingRestartRequiredError) {
        throw error;
      }

      const normalizedError = normalizeDesktopControllerError({
        controllerKind: DESKTOP_ARTIFACT_MEDIA_CONTROLLER_KIND,
        error,
        operation: "initialize_and_start",
        stage: AudioRecordingErrorStage.Start,
      });

      try {
        await options.onFallbackToWebMedia?.({ error: normalizedError });
      } catch (fallbackError) {
        logger.warn({
          message: "[DesktopArtifactMediaController] Failed to handle fallback to web media",
          info: {
            audioRecordingId: options.audioRecordingId,
            error: fallbackError instanceof Error ? fallbackError.message : String(fallbackError),
          },
        });
      }
      throw normalizedError;
    } finally {
      if (initializationTimeout) {
        clearTimeout(initializationTimeout);
      }
    }
  }

  async _initializeAndStart(options: MediaControllerInitializeOptions): Promise<void> {
    await this.initialize(options);
    options.onBeginRecording?.();
    await this.start();
  }

  async initialize(options: MediaControllerInitializeOptions): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    const noteId = options.noteId;
    const audioRecordingId = options.audioRecordingId;

    try {
      if (!audioRecordingId) {
        throw new Error("Desktop recorder requires an associated audio recording.");
      }

      await this.captureService.prepare({
        associatedAudioRecordingId: audioRecordingId,
        calendarEventId: options.calendarEventId,
        associatedNoteId: noteId,
        meetingEventWindowId: options.meetingEventWindowId,
        recordingWindow: options.recordingWindow,
        recordingStrategy: DesktopAudioRecordingStrategy.DesktopArtifact,
        startAttemptTrace: options.startAttemptTrace,
        replacePreviousAudioRecordingId: options.replacePreviousAudioRecordingId,
        abortSignal: this.abortController.signal,
      });

      if (this.abortController.signal.aborted) {
        return;
      }

      await this.ensureVisualizer(DESKTOP_PCM_SAMPLE_RATE);
      this.isInitialized = true;
    } catch (error) {
      throw normalizeDesktopControllerError({
        controllerKind: DESKTOP_ARTIFACT_MEDIA_CONTROLLER_KIND,
        error,
        operation: "initialize",
        stage: AudioRecordingErrorStage.Start,
      });
    }
  }

  async initializeForRecoveredSession({
    audioRecordingId,
  }: {
    audioRecordingId: string;
  }): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    await this.captureService.attach({
      associatedAudioRecordingId: audioRecordingId,
      recordingStrategy: DesktopAudioRecordingStrategy.DesktopArtifact,
    });

    await this.ensureVisualizer(DESKTOP_PCM_SAMPLE_RATE);
    this.isInitialized = true;
  }

  async start(): Promise<void> {
    if (this.abortController.signal.aborted) {
      return;
    }

    await this.transcriber.start();
    this.emitTranscriptionStatus(this.transcriptionMonitor.markConnected());

    if (this.abortController.signal.aborted) {
      return;
    }

    try {
      await this.captureService.start(this.abortController.signal);
    } catch (error) {
      throw normalizeDesktopControllerError({
        controllerKind: DESKTOP_ARTIFACT_MEDIA_CONTROLLER_KIND,
        error,
        operation: "start",
        stage: AudioRecordingErrorStage.CaptureStart,
      });
    }

    if (this.abortController.signal.aborted) {
      return;
    }

    this.handlers.onStart?.();
  }

  async stop(): Promise<void> {
    await this.captureService.stop();
    await this.transcriber.stop();
    this.handlers.onStop?.();
    this.isInitialized = false;
    await this.teardownVisualizer();
  }

  async detachAfterExternalStop(): Promise<void> {
    this.captureService.detachAfterExternalStop();
    await this.transcriber.stop();
    this.isInitialized = false;
    await this.teardownVisualizer();
  }

  async pause(): Promise<void> {
    await this.captureService.pause();
  }

  async resume(): Promise<void> {
    await this.captureService.resume();
  }

  getVisualizer(): AudioLevelProbe | null {
    return this.visualizer;
  }

  dispose(): void {
    this.isInitialized = false;
    void this.transcriber.stop();
    this.transcriber.dispose();
    this.captureService.dispose();
    void this.teardownVisualizer();
  }

  private pushWaveformUpdate(update: DesktopWaveformUpdate): void {
    if (!update.samples.length) {
      return;
    }

    this.probe?.pushWaveformUpdate(update);

    if (this.handlers.onWaveformSamples) {
      const payload: MediaRecorderWaveformSamples = {
        sampleRate: update.sampleRate,
        pcm: update.samples.slice(),
        elapsedDurationMs: update.durationMs,
      };

      this.handlers.onWaveformSamples({ samples: payload });
    }
  }

  private async ensureVisualizer(sampleRate: number): Promise<void> {
    try {
      const { visualizer } = await this.visualizerEngine.start({ sampleRate });

      if (this.abortController.signal.aborted) {
        await this.visualizerEngine.stop();
      }

      this.visualizer = visualizer;
      this.probe = visualizer as DesktopPcmProbe;
    } catch {
      this.visualizer = null;
      this.probe = null;
    }
  }

  private async teardownVisualizer(): Promise<void> {
    this.probe = null;
    this.visualizer = null;
    await this.visualizerEngine.stop();
  }

  private emitTranscriptionStatus(status: TranscriptionStatus | null) {
    if (!status) {
      return;
    }

    this.handlers.onTranscriptionStatusChange?.({ status });
  }
}
